import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  DeleteVibeGitProjectPathVarsSchema,
  DeleteVibeGitProjectResponseSchema,
  GetVibeGitProjectBlobPathVarsSchema,
  GetVibeGitProjectBlobQuerySchema,
  GetVibeGitProjectBlobResponseSchema,
  GetVibeGitProjectTreePathVarsSchema,
  GetVibeGitProjectTreeQuerySchema,
  GetVibeGitProjectTreeResponseSchema,
  ListVibeGitProjectBranchesPathVarsSchema,
  ListVibeGitProjectBranchesResponseSchema,
  ReprovisionVibeGitProjectByIdPathVarsSchema,
  ReprovisionVibeGitProjectByIdResponseSchema
} from "../schemas";

export const VibeGitContentEndpoints = {
  // ---------------------------------------------------------------------------
  // Git CONTENT — read-only file tree / single blob / branches off the
  // tenant's own Forgejo, proxied over its public git edge with the org's own
  // push credential (same trust boundary as a `git clone`). The data source
  // for a dashboard code browser and future agent tooling that reads code
  // without cloning. Every route 409s when the project has not materialized
  // on the tenant's git host yet (PENDING / FAILED / ARCHIVED) — there is no
  // Forgejo repository to read.
  // ---------------------------------------------------------------------------

  /**
   * Read a project's file tree at a ref (branch name or full-length sha).
   * `path`, when given, scopes the result to one subdirectory (filtered
   * server-side — Forgejo's recursive-tree endpoint has no directory-scoped
   * mode). 404 if the ref or path does not exist upstream.
   */
  GetGitProjectTree: {
    method: "GET" as const,
    path: "/api/vibe/git-projects/:id/tree",
    PathVars: GetVibeGitProjectTreePathVarsSchema,
    Query: GetVibeGitProjectTreeQuerySchema,
    Response: ApiSuccessSchema(GetVibeGitProjectTreeResponseSchema)
  },

  /**
   * Read one file's content at a ref. `content` is base64-encoded, matching
   * Forgejo's own Contents API encoding. 404 if the ref or path does not
   * exist upstream (including a path that resolves to a directory, since a
   * blob read has no listing to fall back to).
   */
  GetGitProjectBlob: {
    method: "GET" as const,
    path: "/api/vibe/git-projects/:id/blob",
    PathVars: GetVibeGitProjectBlobPathVarsSchema,
    Query: GetVibeGitProjectBlobQuerySchema,
    Response: ApiSuccessSchema(GetVibeGitProjectBlobResponseSchema)
  },

  /**
   * List a project's branches — the DB-mirrored `refs/heads/*` rows (kept
   * current by the receive-hook on every push), NOT a live Forgejo call.
   * Plain branch names (`"main"`, not `"refs/heads/main"`); tags are
   * excluded. An existing project with no pushes yet returns `[]`.
   */
  ListGitProjectBranches: {
    method: "GET" as const,
    path: "/api/vibe/git-projects/:id/branches",
    PathVars: ListVibeGitProjectBranchesPathVarsSchema,
    Response: ApiSuccessSchema(ListVibeGitProjectBranchesResponseSchema)
  },

  /**
   * Re-arm a FAILED git project by its own id. The standalone counterpart to
   * `ReprovisionGitProject`: a project with no app cannot be reached through
   * the app-scoped retry, so without this a failed code store would be a dead
   * end. 409 unless the project is FAILED.
   */
  ReprovisionGitProjectById: {
    method: "POST" as const,
    path: "/api/vibe/git-projects/:id/reprovision",
    PathVars: ReprovisionVibeGitProjectByIdPathVarsSchema,
    Response: ApiSuccessSchema(ReprovisionVibeGitProjectByIdResponseSchema)
  },

  /**
   * Soft-delete a git project by its own id. Stamps `deletedAt`, flips the
   * row to ARCHIVED, and FREES its name: the org-scoped name unique is
   * unfiltered, so without releasing it the name would stay reserved forever
   * and a later app/git-project create with the same name would 409 with
   * nothing to unclaim it. The row + its refs stay for recoverability; any
   * app that pointed at the project simply reads "no project" thereafter.
   *
   * 404 if the project is absent, already deleted, or owned by another org
   * (a miss is indistinguishable — no leaked deletion signal).
   */
  DeleteGitProject: {
    method: "DELETE" as const,
    path: "/api/vibe/git-projects/:id",
    PathVars: DeleteVibeGitProjectPathVarsSchema,
    Response: ApiSuccessSchema(DeleteVibeGitProjectResponseSchema)
  }
} as const;
